
import { Component, Watch } from 'vue-property-decorator'
import { Mixins } from 'vue-mixin-decorator'
import MixinInterface from '@/_mixins/index'
import ObservationMixin from '@/_mixins/observation'
import HelpText from '@/views/Project/Settings/Components/HelpText.vue'
import { getRequest, patchRequest } from '@/plugins/api-axios'
import { getURL } from '@/utils/axios'
import { deserialise, serialise } from 'kitsu-core'
import { openDialog } from '@/_components/dialog/dialog'
import LeaveDialog from '@/components/leaveDialog.vue'

@Component({
    mixins: [ObservationMixin],
    components: { HelpText },
})
export default class SettingsObservationGeneral extends Mixins<MixinInterface>(ObservationMixin) {
    key = 0

    // GET OBSERVATION PROJECT
    observationProjectSettings: any = null
    observationProjectSettingsBase: any = null
    observationProjectLoading = false
    observationProjectError = false

    initObservationProjectSettings(data = null) {
        if (data) {
            const desData = deserialise(data).data
            if (desData.type == 'project') {
                this.observationProjectSettings = desData.settings.data
            } else if (desData.type == 'projectSettings') {
                this.observationProjectSettings = desData
            }
            this.observationProjectSettingsBase = this.lodash.cloneDeep(
                this.observationProjectSettings
            )
        } else {
            this.observationProjectSettings = this.observationProjectSettingsBase
        }
        this.key = this.key + 1

        this.helpText = this.observationProjectSettings.helpText
            ? { ...this.observationProjectSettings.helpText }
            : null
        this.helpTextChanged = false
    }

    async getObservationProjectLocal() {
        try {
            this.observationProjectLoading = true
            const { data } = await getRequest(
                getURL({
                    path: `projects/${this.currentProjectId}`,
                    base: this.url_observation,
                    params: {
                        include: 'settings',
                        skipLinks: '',
                    },
                })
            )

            this.initObservationProjectSettings(data)
        } catch (e) {
            this.observationProjectError = true
        } finally {
            this.observationProjectLoading = false
        }
    }

    async updateObservationProjectSettings() {
        if (!this.observationProjectSettings) return
        try {
            this.observationProjectLoading = true

            const payload = serialise('projectSettings', {
                id: this.observationProjectSettings.id,
                allowAnonymousObservations:
                    this.observationProjectSettings.allowAnonymousObservations,
                useCost: this.observationProjectSettings.useCost,
                helpText: this.helpText,
                suggestedMaxCategoryDepth:
                    this.observationProjectSettings.suggestedMaxCategoryDepth,
                suggestedMaxLocationDepth:
                    this.observationProjectSettings.suggestedMaxLocationDepth,
            })

            const { data } = await patchRequest(
                getURL({
                    path: `projectSettings/${this.observationProjectSettings.id}`,
                    base: this.url_observation,
                    params: {
                        include: '',
                        skipLinks: '',
                    },
                }),
                {},
                payload.data
            )

            this.initObservationProjectSettings(data)
        } catch (error) {
            this.observationProjectError = true
        } finally {
            this.observationProjectLoading = false
        }
    }

    discardObservationProjectSettings() {
        this.initObservationProjectSettings()
    }

    @Watch('currentProjectId', { immediate: true })
    async watchActiveProject() {
        if (this.currentProjectId) {
            await this.getObservationProjectLocal()
        }
    }

    // HELP TEXT
    helpText: Maybe<Record<string, string>> = null
    loadingHelpText = false
    helpTextChanged = false

    setHelpText(value: Maybe<Record<string, string>>, language: string) {
        this.helpText = value
        this.helpTextChanged = this.observationProjectSettings.helpText
            ? !this.isEqualRichText(
                  this.helpText?.[language],
                  this.observationProjectSettings.helpText[language]
              )
            : Boolean(this.helpText?.[language])
    }

    // LEAVING GUARD
    get stagedChanges() {
        const diff = this.difference(
            this.observationProjectSettings,
            this.observationProjectSettingsBase
        )

        delete diff.type
        delete diff.id
        return diff
    }

    get hasStagedChanges() {
        return this.helpTextChanged || !this.isObjectEmpty(this.stagedChanges)
    }

    // QR code
    qrObservationDescription = ''
    get qrUrlObservation() {
        return `https://internal.byggekortleser.no/api/qrcode/anonymousobservations/${
            this.currentProjectId
        }?size=820${
            this.qrObservationDescription ? '&description=' + this.qrObservationDescription : ''
        }&alpha=true`
    }

    get anonymousObservationUrl() {
        if (!this.currentProjectId) return ''
        const baseUrl = process.env.VUE_APP_BASE_URL || window.location.origin
        const route = this.$router.resolve({
            name: 'add-anonymous-observation',
            params: { id: this.currentProjectId },
        })
        return `${baseUrl}${route.href}`
    }

    copyToClipboard(url: string) {
        if (url) {
            navigator.clipboard.writeText(url)
            this.$store.commit('setSuccessSnackBar', {
                subtitle: this.$t("fe_hse['qr_settings.url_copied_success']"),
            })
        }
    }

    async beforeRouteLeave(to: any, from: any, next: any) {
        if (this.hasStagedChanges) {
            const navigateAway = await openDialog(LeaveDialog, {
                update: this.updateObservationProjectSettings,
                discard: this.discardObservationProjectSettings,
            })

            if (navigateAway) next()
        } else {
            next()
        }
    }
}
